import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ChevronDownIcon, MapPinIcon, MoonIcon, SunIcon } from 'lucide-react';
import { CourseCalendar } from '../components/CourseCalendar';
import { Logo } from '../components/Logo';
import { currentCourse, futureCourses, pastCourses } from '../data/courses';
import { futurePalette, pastPalette } from '../data/courseThemes';
import type { CalendarPalette } from '../data/courseThemes';
import { countSessions, formatRange } from '../utils/calendar';
import type { Course } from '../types/course';
const REGISTRATION_URL = 'https://forms.gle/RU4dDRpzfxv5AceV6';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.65,
    ease: 'easeOut'
  }
};
function CourseHeading({
  course,
  palette
}: {
  course: Course;
  palette?: CalendarPalette;
}) {
  return <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2 mb-6" data-id="element-70" data-mp="1uvjy34wcn5dm">
      <h3 className={`text-2xl font-bold ${palette ? '' : 'text-mountain-darker'}`} style={palette ? {
      color: palette.heading
    } : undefined} data-id="element-71" data-mp="1px8o8u620uve">
        {course.group}
      </h3>
      <span className={`inline-flex items-center gap-1.5 text-sm ${palette ? '' : 'text-mountain-dark/60'}`} style={palette ? {
      color: palette.muted
    } : undefined} data-id="element-72" data-mp="1zp5zu20z5oun">
        <MapPinIcon className="w-3.5 h-3.5" data-id="element-73" data-mp="1kyp1irjmzf1z" />
        {course.location}
      </span>
      {course.note && <span className={`text-xs uppercase tracking-wider font-semibold px-3 py-1 rounded-full ${palette ? '' : 'bg-gold/15 text-gold-dark'}`} style={palette ? {
      background: palette.badgeBg,
      color: palette.badgeText
    } : undefined} data-id="element-74" data-mp="1fxhpuhedhvq1">
          {course.note}
        </span>}
    </div>;
}
export function CoursesPage({
  mode,
  toggleMode,
  onBack
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
  onBack: () => void;
}) {
  const [openPastId, setOpenPastId] = useState<string | null>(null);
  return <main className="w-full bg-sand-lightest min-h-screen" data-id="element-75" data-mp="16alq1mwy7547">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50" style={{
      background: 'rgba(var(--mountain-darker), 0.96)',
      backdropFilter: 'blur(12px)'
    }} data-id="element-76" data-mp="1abdzoh9qfc10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-77" data-mp="1k1gpy9pgybvn">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-78" data-mp="1k3bdzww1ek3y">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-79" data-mp="k1eluuw0bigz" />
            Į pradžią
          </button>
          <Logo tone="light" className="h-8 hidden sm:block" data-id="element-80" data-mp="f2k5dzlii601" />
          <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-81" data-mp="f33goxgnpw53">
            {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-82" data-mp="13aqd391lhxn" /> : <SunIcon className="w-5 h-5" data-id="element-83" data-mp="14vmgxb098xm" />}
          </button>
        </div>
      </header>

      {/* ── PAGE INTRO ── */}
      <section className="pt-20 pb-12 px-4 bg-sand-light" data-id="element-84" data-mp="3nhu3yxq70t32">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-85" data-mp="3ngao388g8k1t">
          <p className="font-accent italic text-gold-dark mb-4 text-xl" data-id="element-86" data-mp="3ikvc6lu04y81">
            Visos grupės vienoje vietoje
          </p>
          <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
          background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
        }} data-id="element-87" data-mp="3eji4s7b9gmz0" />
          <h1 className="text-4xl md:text-5xl font-bold text-mountain-darker mb-5" data-id="element-88" data-mp="1hfzthlzgamb7">
            Kursų kalendorius
          </h1>
          <p className="text-lg text-mountain-dark leading-relaxed font-[300]" data-id="element-89" data-mp="13pe9tv26a8c9">
            Artėjančio kurso tvarkaraštis ir būsimų grupių datos, kad galėtum pasirinkti sau tinkamiausią laiką.
          </p>
        </motion.div>
      </section>

      {/* ── CURRENT COURSE (baltai rožinis, su auksu) ── */}
      <section className="py-20 px-4 bg-sand-lightest" data-id="element-90" data-mp="yrusune3bhgm">
        <div className="max-w-6xl mx-auto" data-id="element-91" data-mp="krk69wjtcp19">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-92" data-mp="ksv83qtp7mpq">
            <p className="text-xs uppercase tracking-widest font-semibold text-gold-dark mb-3" data-id="element-93" data-mp="pqoccebfy6nd">
              Registracija atvira
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-mountain-darker" data-id="element-94" data-mp="2f2yrq15sk2b7">
              Artėjantis kursas
            </h2>
          </motion.div>

          <CourseHeading course={currentCourse} data-id="element-95" data-mp="2u8mgwy12aqns" />
          <CourseCalendar course={currentCourse} data-id="element-96" data-mp="2p8qe3243p59t" />

          <motion.div className="flex justify-center mt-10" {...fadeInUp} data-id="element-97" data-mp="2pa1dyqufdsvn">
            <motion.a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} transition={{
            duration: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-white px-11 py-[15px] rounded-full text-base font-semibold shadow-xl inline-block" style={{
            background: '#1B1B4B'
          }} data-id="element-98" data-mp="330engxmytfn3">
              Registruotis į{' '}
              {currentCourse.group.replace('grupė', 'grupę').toLowerCase()}
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ── FUTURE COURSES ── */}
      <section className="py-20 px-4" style={{
      background: futurePalette.sectionBg
    }} data-id="element-99" data-mp="3mpfwmv3v28dt">
        <div className="max-w-6xl mx-auto" data-id="element-100" data-mp="3rpbro4ukyph8">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-101" data-mp="gogghq20ine">
            <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{
            color: futurePalette.accent
          }} data-id="element-102" data-mp="5g12j093plox">
              Planuok iš anksto
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{
            color: futurePalette.heading
          }} data-id="element-103" data-mp="efntst849b49">
              Būsimi kursai
            </h2>
          </motion.div>

          <div className="space-y-16" data-id="element-104" data-mp="o8len60kzc61">
            {futureCourses.map(course => <div key={course.id} data-id="element-105" data-mp="t7g9x7fqit71">
                <CourseHeading course={course} palette={futurePalette} data-id="element-106" data-mp="x6xfs1z2pscq" />
                <CourseCalendar course={course} palette={futurePalette} showStats={false} data-id="element-107" data-mp="x548qlkjt647" />
              </div>)}
          </div>
        </div>
      </section>

      {/* ── PAST COURSES ── */}
      <section className="py-20 px-4" style={{
      background: pastPalette.sectionBg
    }} data-id="element-108" data-mp="28pcipbc5ox7j">
        <div className="max-w-4xl mx-auto" data-id="element-109" data-mp="23rkt4nyzhn6i">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-110" data-mp="31di29urh4g5s">
            <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{
            color: pastPalette.muted
          }} data-id="element-111" data-mp="2we5hrgo2rl6d">
              Archyvas
            </p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{
            color: pastPalette.heading
          }} data-id="element-112" data-mp="2mp42jspct7tg">
              Praėję kursai
            </h2>
          </motion.div>

          <motion.div className="border rounded-2xl overflow-hidden" style={{
          background: pastPalette.cardBg,
          borderColor: pastPalette.cardBorder
        }} {...fadeInUp} data-id="element-113" data-mp="3fbot4qy09fvy">
            {pastCourses.map((course, i) => {
            const isOpen = openPastId === course.id;
            return <div key={course.id} style={i > 0 ? {
              borderTop: `1px solid ${pastPalette.cardBorder}`
            } : undefined} data-id="element-114" data-mp="2ir53x0qokk88" data-mpi={i}>
                  <button onClick={() => setOpenPastId(isOpen ? null : course.id)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 transition-colors duration-200" style={{
                background: isOpen ? pastPalette.sectionBg : 'transparent'
              }} data-id="element-115" data-mp="2xeq70unk2jg5">
                    <span className="min-w-0" data-id="element-116" data-mp="1ik00kajzp7q3">
                      <span className="block text-base font-semibold" style={{
                    color: pastPalette.heading
                  }} data-id="element-117" data-mp="1efiiqgx6x4hy">
                        {course.group} · {new Date(course.start).getFullYear()}
                      </span>
                      <span className="block text-sm mt-0.5" style={{
                    color: pastPalette.muted
                  }} data-id="element-118" data-mp="zpl2cn5i5j3b">
                        {formatRange(course.start, course.end)} ·{' '}
                        {countSessions(course.start, course.end, course.weekdays)}{' '}
                        užsiėmimai · {course.note}
                      </span>
                    </span>
                    <motion.span animate={{
                  rotate: isOpen ? 180 : 0
                }} transition={{
                  duration: 0.2,
                  ease: [0.23, 1, 0.32, 1]
                }} className="flex-shrink-0" style={{
                  color: pastPalette.accent
                }} data-id="element-119" data-mp="281v1egyr7ew8">
                      <ChevronDownIcon className="w-5 h-5" data-id="element-120" data-mp="1kc0pq2q67wdk" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false} data-id="element-121" data-mp="1feh5mm6skpdp">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" style={{
                  background: pastPalette.sectionBg
                }} data-id="element-122" data-mp="1ag73tsqkaqj4">
                        <div className="px-6 py-8" data-id="element-123" data-mp="2yd6l73tb5xn3">
                          <CourseCalendar course={course} palette={pastPalette} showStats={false} data-id="element-124" data-mp="33c0090o8twpd" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-mountain-darker" data-id="element-125" data-mp="27jmzb20dna4x">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-126" data-mp="23knxhq9xzthp">
          <Logo tone="light" className="h-8" data-id="element-127" data-mp="2hbj0ilxrfgdd" />
          <p className="text-white/50 text-sm" data-id="element-128" data-mp="2hddgrmy36kb4">© 2026 tavo būsena.</p>
          <button onClick={onBack} className="text-white/70 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-129" data-mp="2cdhhu248xj2f">
            Į pradžią
          </button>
        </div>
      </footer>
    </main>;
}